/**
 * Maps Rovo Insights API responses into the feed UI model.
 *
 * The API shape includes explicit `icon` and `color` fields for each insight group;
 * see the backend endpoint introduction for the original response contract.
 *
 * @see https://bitbucket.org/atlassian/conversational-ai-platform/pull-requests/23676
 */
import type {
	InsightsFeedGroup as ApiInsightGroup,
	InsightsFeedInsight as ApiInsight,
	InsightsFeedResponse as ApiInsightResponse,
} from '@atlassian/rovo-insights-service-api/types';

import { resolveInsightIcon, resolveInsightIconAppearance } from './resolveInsightIcon';
import type { InsightItem, InsightSubItem, InsightSummaryDetails } from './types';

// ---------------------------------------------------------------------------
// Mapper Helpers
// ---------------------------------------------------------------------------

function makeId(groupType: string, index: number): string {
	return `${groupType.toLowerCase().replace(/\s+/g, '-')}-${index}`;
}

const MEETING_GROUP_TYPE = 'meeting-insights';
const MEETING_TITLE_SEPARATOR = ' · ';

function isMeetingGroup(group: ApiInsightGroup): boolean {
	return group.type.trim().toLowerCase() === MEETING_GROUP_TYPE;
}

function splitMeetingTitle(title: string): { title: string; titleSecondary?: string } {
	const separatorIndex = title.lastIndexOf(MEETING_TITLE_SEPARATOR);
	if (separatorIndex === -1) {
		return { title };
	}

	const meetingName = title.slice(0, separatorIndex).trim();
	const titleSecondary = title.slice(separatorIndex + MEETING_TITLE_SEPARATOR.length).trim();

	if (!meetingName || !titleSecondary) {
		return { title };
	}

	return {
		title: meetingName,
		titleSecondary,
	};
}

function mapSubItem(
	insight: ApiInsight,
	groupId: string,
	index: number,
	options: { isMeetingGroup: boolean },
): InsightSubItem {
	const titleParts = options.isMeetingGroup
		? splitMeetingTitle(insight.title)
		: { title: insight.title };

	return {
		id: `${groupId}-sub-${index}`,
		...titleParts,
		description: insight.overview ?? '',
		detailsAdf: insight.details_adf,
		people: insight.people?.map((p) => ({
			name: p.name,
			aaid: p.aaid,
			avatarUrl: p.avatar_url,
		})),
		followUps: insight.follow_ups,
		urls: options.isMeetingGroup ? null : insight.urls,
		thinking: insight.thinking,
	};
}

function mapGroup(group: ApiInsightGroup, groupIndex: number): InsightItem {
	const groupId = makeId(group.type, groupIndex);
	const subItems = (group.insights ?? []).map((insight, idx) =>
		mapSubItem(insight, groupId, idx, { isMeetingGroup: isMeetingGroup(group) }),
	);

	return {
		id: groupId,
		title: group.title,
		category: group.type,
		icon: resolveInsightIcon(group.icon),
		iconAppearance: resolveInsightIconAppearance(group.color),
		// Preserve the raw API string keys alongside the resolved values so external
		// consumers (e.g. the home insights carousel) can pass them over the
		// rovo-triggers event bus and re-resolve panel-side. React components and
		// IconTile appearances are not serializable across the bus; the string keys are.
		iconKey: group.icon,
		iconColor: group.color,
		count: group.count,
		subItems,
		debugInfo: group.debug_info
			? {
					generatedAt: group.debug_info.generated_at ?? '',
					prompt: group.debug_info.prompt ?? '',
					llmResponse: group.debug_info.llm_response ?? '',
					error: group.debug_info.error ?? null,
					durationSecs: group.debug_info.duration_secs ?? 0,
					attempts: group.debug_info.attempts ?? 0,
				}
			: undefined,
	};
}

/**
 * Maps the API `summary_details` object into the UI summary model, if present.
 * Returns `undefined` when the backend has not supplied structured summary data
 * so the header can fall back to the flat `summary` string.
 */
function mapSummaryDetails(response: ApiInsightResponse): InsightSummaryDetails | undefined {
	const details = response.summary_details;
	if (!details) {
		return undefined;
	}

	return {
		insightCount: details.insight_count,
		appCount: details.app_count,
		sourceApps: (details.source_apps ?? []).map((app) => ({ id: app.id, name: app.name })),
	};
}

/**
 * Transforms a full API response into an array of InsightItems
 * ready for InsightsFeed / InsightsFeedGroup consumption.
 */
export function mapApiResponseToInsightItems(response: ApiInsightResponse): {
	totalCount: number;
	summary: string;
	summaryDetails: InsightSummaryDetails | undefined;
	generatedAt: string | undefined;
	items: InsightItem[];
} {
	const items = (response.insight_groups ?? []).map(mapGroup);
	const totalCount =
		response.count > 0 ? response.count : items.reduce((sum, item) => sum + item.count, 0);

	return {
		totalCount,
		summary: response.summary,
		summaryDetails: mapSummaryDetails(response),
		generatedAt: response.generated_at,
		items,
	};
}
